<template>
  <div class="flex flex-col gap-y-6 py-4">
    <!-- Dropzone vazia -->
    <div
      v-if="!modelValue"
      data-testid="import-file-dropzone"
      role="button"
      tabindex="0"
      aria-label="Selecionar arquivo CSV"
      class="dropzone-border relative rounded-[24px] bg-primary5/30 p-10 flex flex-col items-center justify-center transition-all cursor-pointer hover:opacity-80"
      @click="triggerFileInput"
      @keydown.enter="triggerFileInput"
      @dragover.prevent
      @drop.prevent="handleFileDrop"
    >
      <input
        ref="fileInput"
        type="file"
        data-testid="import-file-input"
        class="hidden"
        accept=".csv"
        @change="handleFileChange"
      />
      <div class="text-primary30 mb-4">
        <v-icon :icon="mdiTrayArrowUp" size="64" />
      </div>
      <p class="text-[16px] text-textPrimary text-center">
        Arraste o arquivo ou
        <span class="text-primary20 underline">clique aqui</span> para selecionar
      </p>
      <div class="mt-4 text-center text-[14px] text-primary30">
        <p>Formato aceito: .csv</p>
        <p>Máximo de 10.000 linhas e 5 MB</p>
      </div>
    </div>

    <!-- Arquivo selecionado -->
    <div
      v-else
      data-testid="import-file-loaded"
      class="dropzone-border relative rounded-[24px] bg-primary5/30 p-8 flex flex-col items-center justify-center gap-4"
    >
      <div class="text-primary30">
        <v-icon :icon="mdiFileDelimited" size="48" />
      </div>

      <div class="flex flex-col items-center gap-0.5">
        <span class="text-sm font-bold text-textPrimary truncate max-w-full">
          {{ modelValue.name }}
        </span>
        <span class="text-xs text-neutralLight">
          {{ uploading ? uploadProgressLabel : formatSize(modelValue.size) }}
        </span>
      </div>

      <div v-if="uploading" class="w-full text-center">
        <span class="text-sm font-semibold text-primary30">
          {{ uploadProgress >= 99 ? 'Processando no servidor...' : `${uploadProgress}%` }}
        </span>
        <v-progress-linear
          :model-value="uploadProgress"
          :indeterminate="uploadProgress >= 99"
          color="primary"
          rounded
          height="6"
          class="mt-1"
        />
        <p class="text-xs text-neutralLight mt-2">
          {{
            uploadProgress >= 99
              ? 'O arquivo foi enviado e está sendo processado. Este processo pode demorar alguns segundos para arquivos grandes.'
              : 'Não feche esta janela enquanto o envio estiver em andamento.'
          }}
        </p>
      </div>

      <div v-else>
        <button
          type="button"
          data-testid="import-file-remove-button"
          aria-label="Remover arquivo"
          class="absolute top-4 right-4 p-1.5 text-neutralLight hover:text-red-500 rounded-full transition-colors"
          @click="removeFile"
        >
          <v-icon :icon="mdiCloseCircleOutline" size="22" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, h } from 'vue'
import { mdiTrayArrowUp, mdiCloseCircleOutline, mdiFileDelimited } from '@mdi/js'
import { useToast } from 'vue-toastification'
import type { QualificationPolicyType } from './types'
import { DEFAULT_POLICY_TYPE } from './types'

const props = withDefaults(
  defineProps<{
    modelValue: File | null
    uploading?: boolean
    uploadProgress?: number
    uploadLoaded?: number
    uploadTotal?: number
    policyType?: QualificationPolicyType
  }>(),
  {
    uploading: false,
    uploadProgress: 0,
    uploadLoaded: 0,
    uploadTotal: 0,
    policyType: DEFAULT_POLICY_TYPE
  }
)

const uploadProgressLabel = computed(
  () => `${formatSize(props.uploadLoaded)} de ${formatSize(props.uploadTotal)}`
)

const emit = defineEmits<{
  'update:modelValue': [value: File | null]
}>()

const fileInput = ref<HTMLInputElement | null>(null)
const toast = useToast()

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_LINES = 10_000

const UF_ALIASES = new Set(['uf', 'estado', 'state', 'crm_uf', 'uf_crm'])

const BANK_DATA_ALIASES = new Set([
  'dados_bancarios',
  'dados bancarios',
  'banco',
  'bank',
  'bank_data',
  'has_bank_data'
])
const PAYMENT_CRITERIA_ALIASES = new Set([
  'criterio_pagamento',
  'criterio pagamento',
  'pagamento',
  'payment',
  'payment_criteria',
  'has_payment_criteria'
])

const REGISTRATION_FORM_ALIASES = new Set([
  'cadastro_medico',
  'cadastro medico',
  'cadastro',
  'registration_form',
  'has_registration_form'
])
const CONTRACT_ALIASES = new Set(['contrato', 'contract', 'has_contract'])
const DOCUMENTATION_ALIASES = new Set(['documentacao', 'documentation', 'has_documentation'])

const TRANSFER_VALIDATION_SETS = [BANK_DATA_ALIASES, PAYMENT_CRITERIA_ALIASES]
const DOCUMENTAL_VALIDATION_SETS = [
  REGISTRATION_FORM_ALIASES,
  CONTRACT_ALIASES,
  DOCUMENTATION_ALIASES
]

const getValidationSets = () =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? DOCUMENTAL_VALIDATION_SETS
    : TRANSFER_VALIDATION_SETS

const getValidationColumnNames = (): string[] =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? ['cadastro_medico', 'contrato', 'documentacao']
    : ['dados_bancarios', 'criterio_pagamento']

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files?.length) processFile(target.files[0])
}

const handleFileDrop = (e: DragEvent) => {
  if (e.dataTransfer?.files.length) processFile(e.dataTransfer.files[0])
}

interface FileAnalysis {
  lineCount: number
  headerLine: string
}

const analyzeFile = (file: File): Promise<FileAnalysis> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const text = reader.result as string
      const firstNewline = text.indexOf('\n')
      const headerLine = (firstNewline === -1 ? text : text.substring(0, firstNewline)).trim()

      const lines = text.split('\n')
      let lineCount = 0
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) lineCount++
      }

      resolve({ lineCount, headerLine })
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

const normalizeHeader = (raw: string): string =>
  raw
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^["']|["']$/g, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

const isKnownHeader = (col: string): boolean =>
  col === 'crm' || UF_ALIASES.has(col) || getValidationSets().some((set) => set.has(col))

const validateHeaders = (headerLine: string) => {
  const delimiter = headerLine.includes(';') ? ';' : ','
  const headers = headerLine.split(delimiter).map(normalizeHeader).filter(Boolean)

  const seen = new Set<string>()
  const duplicates = headers.filter((col) => {
    if (seen.has(col)) return true
    seen.add(col)
    return false
  })
  if (duplicates.length) {
    const unique = [...new Set(duplicates)]
    return h('span', [
      'Coluna(s) duplicada(s): ',
      ...unique.flatMap((col, i) => [...(i > 0 ? [', '] : []), h('b', col)]),
      '. Cada coluna deve aparecer apenas uma vez.'
    ])
  }

  const unrecognized = headers.filter((col) => !isKnownHeader(col))
  if (unrecognized.length) {
    const validationCols = getValidationColumnNames()
    return h('span', [
      'Coluna(s) não reconhecida(s): ',
      ...unrecognized.flatMap((col, i) => [...(i > 0 ? [', '] : []), h('b', col)]),
      '.',
      h('br'),
      'As colunas aceitas são: ',
      h('b', 'crm'),
      ', ',
      h('b', 'uf'),
      ...validationCols.flatMap((col) => [', ', h('b', col)]),
      '.'
    ])
  }

  const hasCrm = headers.some((col) => col === 'crm')
  if (!hasCrm) return h('span', ['O arquivo não contém a coluna obrigatória ', h('b', 'CRM'), '.'])

  const hasUf = headers.some((col) => UF_ALIASES.has(col))
  if (!hasUf) return h('span', ['O arquivo não contém a coluna obrigatória ', h('b', 'UF'), '.'])

  const hasValidation = headers.some((col) => getValidationSets().some((set) => set.has(col)))
  if (!hasValidation) {
    const validationCols = getValidationColumnNames()
    const colNodes = validationCols.flatMap((col, i) => [
      ...(i > 0 ? [i === validationCols.length - 1 ? ' ou ' : ', '] : []),
      h('b', col)
    ])
    return h('span', ['O arquivo deve conter pelo menos uma das colunas: ', ...colNodes, '.'])
  }

  return null
}

const rejectFile = (message: string | ReturnType<typeof h>) => {
  toast.error(message)
  if (fileInput.value) fileInput.value.value = ''
}

const processFile = async (file: File) => {
  const acceptedMimeTypes = ['text/csv', 'application/vnd.ms-excel']
  const hasCsvExtension = file.name.toLowerCase().endsWith('.csv')
  const hasValidMime = !file.type || acceptedMimeTypes.includes(file.type)
  if (!hasCsvExtension || !hasValidMime) {
    rejectFile('Formato inválido. Apenas arquivos .csv são aceitos.')
    return
  }

  if (file.size > MAX_FILE_SIZE) {
    rejectFile('O arquivo excede o limite de 5MB.')
    return
  }

  try {
    const { lineCount, headerLine } = await analyzeFile(file)

    if (lineCount > MAX_LINES) {
      rejectFile(`O arquivo excede o limite de ${MAX_LINES.toLocaleString('pt-BR')} linhas.`)
      return
    }

    const headerError = validateHeaders(headerLine)
    if (headerError) {
      rejectFile(headerError)
      return
    }
  } catch {
    rejectFile('Erro ao ler o arquivo.')
    return
  }

  emit('update:modelValue', file)
}

const removeFile = () => {
  emit('update:modelValue', null)
  if (fileInput.value) fileInput.value.value = ''
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}
</script>

<style scoped>
.dropzone-border {
  border: 4px dashed theme('colors.primary30');
}
</style>
